import { X } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  AccentButtonOutline,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import {
  CardControlsApiFlags,
  DebitCardWithTruck,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { InputNumber } from 'antd';
import {
  formatCurrencyForAntd,
  parseCurrencyFromAntd,
} from '../../../../../../../utils/currencyFormat';

type FormValues = {
  dailySpendLimit: number | ''; // typed to allow empty string while typing
  thirtyDaySpendLimit: number | '';
};

export const EditSpendLimitModal = ({
  isOpen,
  onClose,
  cards,
  loadingCards,
  selectedCard,
}: {
  isOpen: boolean;
  onClose: () => void;
  cards: DebitCardWithTruck[];
  loadingCards: boolean;
  selectedCard?: DebitCardWithTruck;
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    control,
    reset,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      dailySpendLimit: selectedCard?.daily_spend_limit ?? 0,
      thirtyDaySpendLimit: selectedCard?.thirty_day_spend_limit ?? 0,
    },
  });

  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const { userId } = useUser();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // keep defaults in sync with newly selected card
  useEffect(() => {
    if (!selectedCard) return;
    reset(
      {
        dailySpendLimit: selectedCard.daily_spend_limit ?? 0,
        thirtyDaySpendLimit: selectedCard.thirty_day_spend_limit ?? 0,
      },
      { keepErrors: false }
    );
  }, [selectedCard, reset]);

  const dailyLimit = watch('dailySpendLimit');
  const thirtyDayLimit = watch('thirtyDaySpendLimit');

  // Live validation: 30‑day >= daily
  useEffect(() => {
    const d = Number(dailyLimit || 0);
    const m = Number(thirtyDayLimit || 0);
    if (m < d) {
      setError('thirtyDaySpendLimit', {
        type: 'validate',
        message:
          '30‑Day limit must be greater than or equal to the Daily limit.',
      });
    } else {
      clearErrors('dailySpendLimit');
      clearErrors('thirtyDaySpendLimit');
    }
  }, [dailyLimit, thirtyDayLimit, setError, clearErrors]);

  const unchanged = useMemo(() => {
    if (!selectedCard) return true;
    const d = Number(dailyLimit || 0);
    const m = Number(thirtyDayLimit || 0);
    return (
      d === (selectedCard.daily_spend_limit ?? 0) &&
      m === (selectedCard.thirty_day_spend_limit ?? 0)
    );
  }, [dailyLimit, thirtyDayLimit, selectedCard]);

  const disableSave =
    isSubmitting ||
    unchanged ||
    !!errors.thirtyDaySpendLimit ||
    !!errors.dailySpendLimit;

  const updateSpendLimits = async (debit_card_id: string, data: FormValues) => {
    const response = await callProtectedApi(
      `/debitCards/${debit_card_id}/controls`,
      'post',
      {
        api_flag: CardControlsApiFlags.SpendAmount,
        body: {
          daily_spend_limit: Number(data.dailySpendLimit) * 100, // cents
          thirty_day_spend_limit: Number(data.thirtyDaySpendLimit) * 100,
        },
      },
      'Error occurred while updating spend limits.'
    );
    if (response?.status === 200) {
      toast.success('Spend limits updated successfully');
      // Reflect the change on-screen immediately. BP applies control changes
      // slightly async, so an immediate refetch can still return the old
      // values — patch the cache optimistically instead. data.* is in the same
      // units as the card fields (the form was seeded from them). A later
      // natural refetch reconciles with the server.
      queryClient.setQueryData<DebitCardWithTruck[]>(
        ['debitCards', userId],
        (old) =>
          old?.map((card) =>
            card.debit_card_id === debit_card_id
              ? {
                  ...card,
                  daily_spend_limit: Number(data.dailySpendLimit),
                  thirty_day_spend_limit: Number(data.thirtyDaySpendLimit),
                }
              : card
          ) ?? old
      );
      onClose();
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!selectedCard?.debit_card_id) return;

    const d = Number(data.dailySpendLimit);
    const m = Number(data.thirtyDaySpendLimit);

    // Submit‑time guards (in case user bypasses UI)
    if (!Number.isFinite(d) || d <= 0) {
      setError('dailySpendLimit', {
        type: 'validate',
        message: 'Enter a valid positive number.',
      });
      toast.error('Please enter a valid Daily Spend Limit.');
      return;
    }
    if (!Number.isFinite(m) || m <= 0) {
      setError('thirtyDaySpendLimit', {
        type: 'validate',
        message: 'Enter a valid positive number.',
      });
      toast.error('Please enter a valid 30‑Day Spend Limit.');
      return;
    }
    if (m < d) {
      setError('thirtyDaySpendLimit', {
        type: 'validate',
        message:
          '30‑Day limit must be greater than or equal to the Daily limit.',
      });
      toast.error('30‑Day limit can’t be less than the Daily limit.');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateSpendLimits(selectedCard.debit_card_id, data);
    } catch (e) {
      toast.error('Failed to update spend limits');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetDaily = () => {
    setValue('dailySpendLimit', 1000, {
      shouldValidate: true,
      shouldDirty: true,
    });
    clearErrors(['dailySpendLimit', 'thirtyDaySpendLimit']);
  };

  const resetThirty = () => {
    setValue('thirtyDaySpendLimit', 10000, {
      shouldValidate: true,
      shouldDirty: true,
    });
    clearErrors(['dailySpendLimit', 'thirtyDaySpendLimit']);
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50"
            onClick={isSubmitting ? undefined : onClose}
          >
            <div
              className="h-auto max-h-[90%] w-full max-w-[95%] overflow-x-hidden rounded-[4px] bg-white md:max-w-[520px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-[56px] w-full items-center justify-between p-4">
                <h2 className="text-[16px] font-[600] text-primary">
                  Edit Spend Limits
                </h2>
                <X
                  className={
                    isSubmitting
                      ? 'pointer-events-none opacity-40'
                      : 'cursor-pointer hover:opacity-80'
                  }
                  onClick={isSubmitting ? undefined : onClose}
                  size={24}
                />
              </div>

              {!loadingCards ? (
                <form
                  className="flex flex-col gap-10 border border-bordergray p-6 py-10"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <p className="text-[16px] text-graysecondary">
                    Set spending limits for this card. Limits apply to all
                    purchases made with this card.
                  </p>

                  {/* Daily */}
                  <div className="flex flex-col gap-2">
                    <div className="flex w-full flex-col items-start justify-between md:flex-row md:items-center">
                      <label className="mb-1 block text-[16px] font-[600] text-primary">
                        Daily Spend Limit
                      </label>
                      <AccentButtonOutline
                        addClass="rounded-full h-[32px] w-[150px] text-[14px] font-[400]"
                        onClick={resetDaily}
                      >
                        Reset to Default
                      </AccentButtonOutline>
                    </div>
                    <Controller
                      name="dailySpendLimit"
                      control={control}
                      rules={{
                        required: 'Daily Spend Limit is required',
                        min: {
                          value: 1,
                          message: 'Daily Spend Limit must be greater than 0',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <div
                          className={`mt-1 w-full !rounded-md border p-1 focus:outline-none ${
                            errors.dailySpendLimit
                              ? 'border-red-500'
                              : 'border-bordergray'
                          }`}
                        >
                          <InputNumber
                            id="dailySpendLimit"
                            className="w-full border-0"
                            placeholder="Ex. $ 100,000"
                            formatter={(v) => formatCurrencyForAntd(v)}
                            parser={(v) => parseCurrencyFromAntd(v)}
                            min={0}
                            controls={false}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                          />
                        </div>
                      )}
                    />

                    {errors.dailySpendLimit && (
                      <p id="daily-error" className="text-sm text-red-600">
                        {String(errors.dailySpendLimit.message)}
                      </p>
                    )}
                  </div>

                  {/* 30‑Day */}
                  <div className="flex flex-col gap-2">
                    <div className="flex w-full flex-col items-start justify-between md:flex-row md:items-center">
                      <label className="mb-1 block text-[16px] font-[600] text-primary">
                        30‑Day Spend Limit
                      </label>
                      <AccentButtonOutline
                        addClass="rounded-full h-[32px] w-[150px] text-[14px] font-[400]"
                        onClick={resetThirty}
                      >
                        Reset to Default
                      </AccentButtonOutline>
                    </div>
                    <Controller
                      name="thirtyDaySpendLimit"
                      control={control}
                      rules={{
                        required: '30-Day Spend Limit is required',
                        min: {
                          value: 1,
                          message: '30-Day Spend Limit must be greater than 0',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <div
                          className={`mt-1 w-full !rounded-md border p-1 focus:outline-none ${
                            errors.dailySpendLimit
                              ? 'border-red-500'
                              : 'border-bordergray'
                          }`}
                        >
                          <InputNumber
                            id="thirtyDaySpendLimit"
                            className="w-full border-0"
                            placeholder="Ex. $ 100,000"
                            formatter={(v) => formatCurrencyForAntd(v)}
                            parser={(v) => parseCurrencyFromAntd(v)}
                            min={0}
                            controls={false}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            status={
                              errors.thirtyDaySpendLimit ? 'error' : undefined
                            }
                            ref={ref}
                          />
                        </div>
                      )}
                    />

                    {errors.thirtyDaySpendLimit && (
                      <p id="thirty-error" className="text-sm text-red-600">
                        {String(errors.thirtyDaySpendLimit.message)}
                      </p>
                    )}
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-center p-12">
                  <ClipLoader color="#1D213B" loading size={60} />
                </div>
              )}

              {/* Footer buttons */}
              <div className="flex justify-between space-x-4 p-4">
                <NeutralButton
                  onClick={onClose}
                  disabled={isSubmitting}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit
                  disabled={disableSave}
                  onClick={handleSubmit(onSubmit)}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600"
                >
                  {!isSubmitting ? (
                    'Save'
                  ) : (
                    <ClipLoader color="#1D213B" size={20} />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;
  return <>{renderScreenView(screenView)}</>;
};

export default EditSpendLimitModal;
